import { useEffect, useState } from "react";
import MoonMark from "@/components/MoonMark";

const API = process.env.REACT_APP_BACKEND_URL;

const AdminVideos = () => {
  const [videos, setVideos] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [title, setTitle] = useState("");
  const [link, setLink] = useState("");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [edits, setEdits] = useState({});

  const load = async () => {
    try {
      const res = await fetch(`${API}/api/videos`);
      const data = await res.json();
      setVideos(data);
      setLoaded(true);
    } catch {
      setError("Could not load the screenings.");
      setLoaded(true);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const authHeaders = () => {
    const token = localStorage.getItem("nox_admin_token") || "";
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const changed = () => window.dispatchEvent(new Event("nox-videos-changed"));

  const add = async (e) => {
    e.preventDefault();
    setError("");
    if (!link.trim()) {
      setError("Paste a YouTube link first.");
      return;
    }
    setBusy("add");
    try {
      const res = await fetch(`${API}/api/admin/videos`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        credentials: "include",
        body: JSON.stringify({ title, url: link }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Add failed");
      setTitle("");
      setLink("");
      await load();
      changed();
    } catch (err) {
      setError(err.message || "Add failed.");
    } finally {
      setBusy("");
    }
  };

  const act = async (endpoint, body) => {
    setError("");
    try {
      const res = await fetch(`${API}${endpoint}`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        credentials: "include",
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Action failed");
      await load();
      changed();
    } catch (err) {
      setError(err.message || "Action failed.");
    }
  };

  const del = async (id) => {
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/videos/${id}`, {
        method: "DELETE",
        credentials: "include",
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error("Delete failed");
      await load();
      changed();
    } catch (err) {
      setError(err.message || "Delete failed.");
    }
  };

  return (
    <div className="mt-10" data-testid="admin-videos">
      <form onSubmit={add} className="border border-gold/30 bg-[#0d0d12] p-6" data-testid="videos-upload">
        <h3 className="font-display text-sm tracking-[0.2em] text-silver">Add a screening</h3>
        <p className="mt-2 text-xs text-slate-500">
          Paste any YouTube link — watch, share, Shorts or embed URLs all work. It joins the end; reorder below.
        </p>
        <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <input
            className="input-night"
            placeholder="Video title (e.g. Showreel)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            data-testid="videos-upload-title"
          />
          <input
            className="input-night"
            placeholder="https://www.youtube.com/watch?v=…"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            data-testid="videos-upload-link"
          />
        </div>
        {error && (
          <p className="mt-3 text-xs text-red-500" data-testid="videos-error">
            {error}
          </p>
        )}
        <button className="btn-night mt-5" type="submit" disabled={busy === "add"} data-testid="videos-upload-btn">
          {busy === "add" ? "Adding…" : "Add Screening"}
        </button>
      </form>

      <div className="mt-8 space-y-3">
        {!loaded && (
          <div className="flex items-center justify-center gap-4 py-10">
            <MoonMark className="h-6 w-6 animate-spin text-gold" style={{ animationDuration: "6s" }} />
            <span className="font-jmono text-[10px] uppercase tracking-[0.3em] text-slate-500">Summoning…</span>
          </div>
        )}
        {loaded && videos.length === 0 && (
          <p className="border border-line bg-surface p-6 text-center font-cormorant text-lg italic text-slate-500">
            No screenings yet — paste a YouTube link above.
          </p>
        )}
        {videos.map((v, i) => (
          <div key={v.id} className="flex flex-wrap items-center gap-3 border border-line bg-surface p-4" data-testid={`video-row-${i}`}>
            <span className="font-display text-lg text-gold/60">{String(i + 1).padStart(2, "0")}</span>
            <img
              src={`https://i.ytimg.com/vi/${v.youtube_id}/mqdefault.jpg`}
              alt=""
              className="h-9 w-16 shrink-0 border border-line object-cover"
            />
            <input
              className="input-night min-w-0 flex-1 !py-2 text-sm"
              value={edits[v.id] ?? v.title}
              onChange={(e) => setEdits({ ...edits, [v.id]: e.target.value })}
              data-testid={`video-title-input-${i}`}
            />
            <button
              className="btn-ghost-night cursor-pointer !px-3 !py-1.5 !text-[9px]"
              onClick={() => act(`/api/admin/videos/${v.id}/rename`, { title: edits[v.id] ?? v.title })}
              disabled={busy !== ""}
              data-testid={`video-save-${i}`}
            >
              Save
            </button>
            <div className="flex gap-2">
              <button
                className="flex h-8 w-8 cursor-pointer items-center justify-center border border-line text-slate-400 transition-colors hover:border-gold/50 hover:text-gold disabled:opacity-30"
                onClick={() => act(`/api/admin/videos/${v.id}/move`, { direction: "up" })}
                disabled={i === 0 || busy !== ""}
                aria-label={`Move ${v.title} up`}
                data-testid={`video-up-${i}`}
              >
                ▲
              </button>
              <button
                className="flex h-8 w-8 cursor-pointer items-center justify-center border border-line text-slate-400 transition-colors hover:border-gold/50 hover:text-gold disabled:opacity-30"
                onClick={() => act(`/api/admin/videos/${v.id}/move`, { direction: "down" })}
                disabled={i === videos.length - 1 || busy !== ""}
                aria-label={`Move ${v.title} down`}
                data-testid={`video-down-${i}`}
              >
                ▼
              </button>
            </div>
            <button
              className="cursor-pointer border border-red-900/60 px-3 py-1.5 font-jmono text-[9px] uppercase tracking-[0.25em] text-red-400 transition-colors hover:bg-red-900/20"
              onClick={() => del(v.id)}
              disabled={busy !== ""}
              data-testid={`video-del-${i}`}
            >
              Remove
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminVideos;
